import { useState } from 'react';
import { useStaff, useRoles, useCreateStaff, useUpdateStaff, useDeleteStaff } from './hooks/useStaff';
import { twoFactorApiClient } from '../auth/api/twoFactorApiClient';
import { handleApiError } from '../../shared/utils/handleApiError';
import { useToast } from '../../shared/components/toast/useToast';
import { usePermissions, Permission } from '../../shared/hooks/usePermissions';
import Button from '../../shared/components/Button';
import type { StaffMember } from './types';

const inputCls = 'w-full bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const labelCls = 'block text-sm font-medium text-foreground mb-1';

function formatRole(name: string) {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(s: string | null | undefined) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Create modal ─────────────────────────────────────────────────────────────

function CreateStaffModal({ onClose }: { onClose: () => void }) {
  const { data: roles = [] } = useRoles();
  const createMutation = useCreateStaff();
  const [form, setForm] = useState({ fullName: '', email: '', password: '', roleId: '' });
  const [error, setError] = useState('');

  const collectiveRoles = roles.filter((r) => r.scope === 'COLLECTIVE');

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError('');
    if (!form.fullName.trim()) return setError('Full name is required');
    if (!form.email.trim()) return setError('Email is required');
    if (form.password.length < 8) return setError('Password must be at least 8 characters');
    if (!form.roleId) return setError('Role is required');
    try {
      await createMutation.mutateAsync({ fullName: form.fullName.trim(), email: form.email.trim(), password: form.password, roleId: form.roleId });
      onClose();
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Something went wrong');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg w-full max-w-md flex flex-col">
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-lg font-bold text-foreground">Add Staff Member</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-muted-foreground">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" /></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className={labelCls}>Full Name</label>
            <input className={inputCls} required placeholder="e.g. Amaka Obi" value={form.fullName} onChange={(e) => set('fullName', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input className={inputCls} required type="email" placeholder="e.g. amaka@thecollective.com" value={form.email} onChange={(e) => set('email', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Role</label>
            <select className={inputCls} required value={form.roleId} onChange={(e) => set('roleId', e.target.value)}>
              <option value="">Select a role…</option>
              {collectiveRoles.map((r) => (
                <option key={r.id} value={r.id}>{formatRole(r.name)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Temporary Password</label>
            <input className={inputCls} required type="password" minLength={8} placeholder="Min. 8 characters" value={form.password} onChange={(e) => set('password', e.target.value)} />
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-md hover:bg-muted">
              Cancel
            </button>
            <Button loading={createMutation.isPending} onClick={handleSubmit}>
              Add Member
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Edit modal ───────────────────────────────────────────────────────────────

function EditStaffModal({ member, onClose }: { member: StaffMember; onClose: () => void }) {
  const { data: roles = [] } = useRoles();
  const updateMutation = useUpdateStaff();
  const [form, setForm] = useState({ fullName: member.fullName, roleId: member.role.id });
  const [error, setError] = useState('');

  const collectiveRoles = roles.filter((r) => r.scope === 'COLLECTIVE');
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError('');
    if (!form.fullName.trim()) return setError('Full name is required');
    if (!form.roleId) return setError('Role is required');
    try {
      await updateMutation.mutateAsync({ id: member.id, data: { fullName: form.fullName.trim(), roleId: form.roleId } });
      onClose();
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Something went wrong');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg w-full max-w-md flex flex-col">
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-lg font-bold text-foreground">Edit Staff Member</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-muted-foreground">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" /></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className={labelCls}>Full Name</label>
            <input className={inputCls} required value={form.fullName} onChange={(e) => set('fullName', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input className={`${inputCls} bg-muted text-muted-foreground`} value={member.email} disabled />
          </div>
          <div>
            <label className={labelCls}>Role</label>
            <select className={inputCls} value={form.roleId} onChange={(e) => set('roleId', e.target.value)}>
              {collectiveRoles.map((r) => (
                <option key={r.id} value={r.id}>{formatRole(r.name)}</option>
              ))}
            </select>
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-md hover:bg-muted">
              Cancel
            </button>
            <Button loading={updateMutation.isPending} onClick={handleSubmit}>
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Delete confirmation ──────────────────────────────────────────────────────

function DeleteConfirm({ member, onClose }: { member: StaffMember; onClose: () => void }) {
  const deleteMutation = useDeleteStaff();

  const handleConfirm = async () => {
    await deleteMutation.mutateAsync(member.id);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg w-full max-w-sm p-6 space-y-4">
        <h2 className="text-lg font-bold text-foreground">Remove staff member?</h2>
        <p className="text-sm text-foreground">
          <strong>{member.fullName}</strong> will be removed from TCS staff. Any merchants they're assigned to as BPM Officer or Liaison will be unaffected but will show as unassigned.
        </p>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-md hover:bg-muted">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={deleteMutation.isPending}
            className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50">
            {deleteMutation.isPending ? 'Removing…' : 'Remove'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── 2FA reset confirmation ───────────────────────────────────────────────────

/**
 * Clears a staff member's two-step verification (PE-2157) so they can enrol
 * again — the recovery path when an authenticator is lost, and currently the
 * ONLY way out of the mandated-but-never-enrolled lockout (watchman refuses to
 * issue the token that enrolment itself requires; see twoFactorApiClient).
 *
 * Not routed through useMutation: watchman answers 204 with no body, and there
 * is no cached 2FA state to invalidate — the users list does not carry
 * `twoFactorEnabled`, so nothing on this page changes appearance afterwards.
 */
/**
 * Two-step verification state for one staff member (PE-2232).
 *
 * Three states, not two: watchman only started returning `twoFactorEnabled` on
 * `GET /users/staff` in PE-2232, so an environment on an older build omits it.
 * Rendering that absence as "Off" would be a lie — and would also hide the
 * "Reset 2FA" action for people who do have it on — so it renders as "Unknown".
 */
function TwoFactorBadge({ member }: { member: StaffMember }) {
  if (member.twoFactorEnabled == null) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
        Unknown
      </span>
    );
  }
  if (!member.twoFactorEnabled) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        Off
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
      title={member.twoFactorEnrolledAt ? `Enrolled ${formatDate(member.twoFactorEnrolledAt)}` : undefined}
    >
      On
    </span>
  );
}

function ResetTwoFactorConfirm({ member, onClose }: { member: StaffMember; onClose: () => void }) {
  const { toast } = useToast();
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setIsBusy(true);
    setError('');
    try {
      await twoFactorApiClient.adminReset(member.id);
      toast.success(`Two-step verification reset for ${member.fullName}`);
      onClose();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg w-full max-w-sm p-6 space-y-4">
        <h2 className="text-lg font-bold text-foreground">Reset two-step verification?</h2>
        <p className="text-sm text-foreground">
          <strong>{member.fullName}</strong> will be able to sign in with their password alone
          until they set up an authenticator again. Use this only after confirming who you are
          talking to.
        </p>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-md hover:bg-muted">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isBusy}
            className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50">
            {isBusy ? 'Resetting…' : 'Reset'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function StaffPage() {
  const { data: staff = [], isLoading, error } = useStaff();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.STAFF_MANAGE);

  const [showCreate, setShowCreate] = useState(false);
  const [resettingTwoFactor, setResettingTwoFactor] = useState<StaffMember | undefined>();
  const [editing, setEditing] = useState<StaffMember | undefined>();
  const [deleting, setDeleting] = useState<StaffMember | undefined>();

  return (
    <div className="space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">TCS Team</h1>
          <p className="text-sm text-foreground mt-1">
            Staff who can be assigned as BPM Officers and Liaisons on merchant accounts
          </p>
        </div>
        {canManage && <Button onClick={() => setShowCreate(true)}>Add Staff Member</Button>}
      </div>

      {isLoading && <div className="text-center py-12 text-foreground">Loading staff…</div>}
      {error && <div className="text-center py-12 text-red-500">Failed to load staff.</div>}

      {!isLoading && !error && (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted">
                <th className="text-left px-4 py-3 font-semibold text-foreground">Name</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground">Email</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground">Role</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground">2FA</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground">Added</th>
                {canManage && <th className="text-left px-4 py-3 font-semibold text-foreground">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {staff.length === 0 ? (
                <tr>
                  <td colSpan={canManage ? 6 : 5} className="text-center py-12 text-foreground">
                    No staff members yet.
                  </td>
                </tr>
              ) : (
                staff.map((member) => (
                  <tr key={member.id} className="border-b border-border hover:bg-muted">
                    <td className="px-4 py-3 font-medium text-foreground">{member.fullName}</td>
                    <td className="px-4 py-3 text-foreground">{member.email}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-foreground">
                        {formatRole(member.role.name)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <TwoFactorBadge member={member} />
                    </td>
                    <td className="px-4 py-3 text-foreground">{formatDate(member.createdAt)}</td>
                    {canManage && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <button onClick={() => setEditing(member)} className="text-sm text-blue-600 hover:underline">
                            Edit
                          </button>
                          {/* STAFF_MANAGE is a sufficient gate for the 2FA reset:
                              only COLLECTIVE_ADMIN and COLLECTIVE_SUPERADMIN hold it,
                              and both are COLLECTIVE-scoped with USER_UPDATE — which is
                              exactly what watchman's adminReset requires.
                              Hidden only when we KNOW 2FA is off — there is nothing to
                              reset then. An older watchman omits the field, and in that
                              case the button stays (matching the pre-PE-2232 behaviour)
                              rather than disappearing for everyone. */}
                          {member.twoFactorEnabled !== false && (
                            <button onClick={() => setResettingTwoFactor(member)} className="text-sm text-blue-600 hover:underline">
                              Reset 2FA
                            </button>
                          )}
                          <button onClick={() => setDeleting(member)} className="text-sm text-red-600 hover:underline">
                            Remove
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <CreateStaffModal onClose={() => setShowCreate(false)} />}
      {editing && <EditStaffModal member={editing} onClose={() => setEditing(undefined)} />}
      {deleting && <DeleteConfirm member={deleting} onClose={() => setDeleting(undefined)} />}
      {resettingTwoFactor && (
        <ResetTwoFactorConfirm member={resettingTwoFactor} onClose={() => setResettingTwoFactor(undefined)} />
      )}
    </div>
  );
}
